import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, FileSignature, Loader2, PlusCircle, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { draftsApi } from '../../services/api';
import { useDraftsStore } from '../../store/draftsStore';
import { Draft, DRAFT_TYPE_LABELS } from '../../types';

interface DraftsPanelProps {
  jobId: string;
  /** Memo title — carried to the drafting intake as the attached-memo label. */
  jobTitle?: string;
}

/**
 * The memo's Drafts tab (spec §4.4) — a LIST of this memo's drafts plus
 * "New draft from this memo", which opens the ONE unified drafting intake
 * (/app/drafts/new) with the memo pre-attached. The legacy type-grid +
 * textarea intake that used to live here is retired; rows open the canonical
 * draft viewer at /app/drafts/:id.
 */
export function DraftsPanel({ jobId, jobTitle }: DraftsPanelProps) {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { types, fetchTypes, removeDraft } = useDraftsStore();

  useEffect(() => {
    fetchTypes();
    draftsApi
      .list(jobId)
      .then(({ data }) => setDrafts(data))
      .catch(() => {/* list failure is non-fatal; the user can still create */})
      .finally(() => setIsLoading(false));
  }, [jobId, fetchTypes]);

  const labelFor = (type: string) =>
    types.find((t) => t.type === type)?.label ?? DRAFT_TYPE_LABELS[type] ?? type;

  const startNewDraft = () =>
    navigate('/app/drafts/new', { state: { memoJobId: jobId, memoTitle: jobTitle } });

  const handleDownload = async (draft: Draft) => {
    setDownloadingId(draft.id);
    try {
      const response = await draftsApi.downloadDocx(jobId, draft.id);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${draft.title.replace(/\s+/g, '-').toLowerCase()}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (draft: Draft) => {
    if (!confirm('Delete this draft?')) return;
    try {
      await draftsApi.remove(jobId, draft.id);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      removeDraft(draft.id);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete draft');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-legal-200 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-legal-900">Drafts from this memo</h3>
          <p className="text-sm text-legal-500 mt-0.5">
            Cited from your memo — each draft uses only the authorities this research verified.
            2 drafts included with every research memo.
          </p>
        </div>
        <button
          onClick={startNewDraft}
          data-track="memo-new-draft"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors shrink-0"
        >
          <PlusCircle className="h-4 w-4" />
          <span>New draft from this memo</span>
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {isLoading ? (
        <div className="bg-white rounded-lg border border-legal-200 p-6 text-sm text-legal-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading drafts…
        </div>
      ) : drafts.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed border-legal-300 p-8 text-center">
          <FileSignature className="h-8 w-8 text-legal-300 mx-auto mb-2" />
          <p className="text-sm text-legal-600 font-medium">No drafts from this memo yet</p>
          <p className="text-xs text-legal-400 mt-1">
            Notices, petitions, applications — pick a document type and the interview fills in
            the rest from this memo.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-legal-200 divide-y divide-legal-100">
          {drafts.map((draft) => (
            <div key={draft.id} className="flex items-center justify-between p-4">
              <Link
                to={`/app/drafts/${draft.id}`}
                data-track="memo-draft-row"
                className="text-left flex-1 min-w-0 mr-3"
              >
                <p className="text-sm font-medium text-legal-900 truncate hover:text-accent-700 transition-colors">
                  {draft.title}
                </p>
                <p className="text-xs text-legal-400 mt-0.5">
                  {labelFor(draft.draft_type)} ·{' '}
                  {formatDistanceToNow(new Date(draft.created_at), { addSuffix: true })}
                </p>
              </Link>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleDownload(draft)}
                  disabled={downloadingId === draft.id}
                  className="p-2 text-legal-400 hover:text-legal-700 hover:bg-legal-50 rounded-md transition-colors disabled:opacity-60"
                  title="Download as Word"
                >
                  {downloadingId === draft.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(draft)}
                  className="p-2 text-legal-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  title="Delete draft"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
